import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search, Shield, Star, MapPin, CheckCircle, Circle, Filter, TrendingUp
} from 'lucide-react';
import { getInitials } from '@/lib/utils';

const ratingTier = (rating) => {
  const r = rating ?? 0;
  if (r >= 4.5) return { label: 'Top', accent: 'from-emerald-500 to-teal-500', text: 'text-emerald-600' };
  if (r >= 4) return { label: 'Eccellente', accent: 'from-blue-500 to-indigo-500', text: 'text-blue-600' };
  if (r >= 3) return { label: 'Buono', accent: 'from-amber-500 to-orange-500', text: 'text-amber-600' };
  if (r > 0) return { label: 'Discreto', accent: 'from-slate-500 to-slate-600', text: 'text-slate-600' };
  return { label: 'Nuovo', accent: 'from-violet-500 to-purple-600', text: 'text-violet-600' };
};

const Stars = ({ value = 0, size = 'h-3 w-3' }) => {
  const filled = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          className={`${size} ${n <= filled ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`}
        />
      ))}
    </div>
  );
};

const RefereeCard = ({ referee, onClick }) => {
  const tier = ratingTier(referee.average_rating);
  const isAvailable = referee.is_available;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full group"
    >
      <Card className="overflow-hidden h-full hover:shadow-xl hover:-translate-y-1 transition-all">
        <CardContent className="p-0 h-full">
          {/* Top gradient banner */}
          <div className={`relative h-20 bg-gradient-to-br ${tier.accent}`}>
            <div className="pointer-events-none absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/15 blur-2xl" />
            <div className="absolute top-2.5 right-2.5">
              <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-white/25 backdrop-blur text-white">
                {tier.label}
              </span>
            </div>
            <div className="absolute top-2.5 left-2.5">
              <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 backdrop-blur ${isAvailable ? 'bg-emerald-500/30 text-white ring-1 ring-emerald-300/40' : 'bg-black/30 text-white/80'}`}>
                {isAvailable ? <CheckCircle className="h-2.5 w-2.5" /> : <Circle className="h-2.5 w-2.5" />}
                {isAvailable ? 'Disponibile' : 'Occupato'}
              </span>
            </div>
          </div>

          {/* Avatar overlapping */}
          <div className="px-4 -mt-9 relative">
            <Avatar className="h-16 w-16 ring-4 ring-card shadow-lg">
              <AvatarImage src={referee.user?.picture} />
              <AvatarFallback className="text-lg">{getInitials(referee.user?.name)}</AvatarFallback>
            </Avatar>
          </div>

          <div className="p-4 pt-2 space-y-3">
            <div>
              <h3 className="font-bold text-lg leading-tight group-hover:text-primary transition-colors">
                {referee.user?.name}
              </h3>
              {referee.city && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3" />
                  {referee.city}
                  {referee.coverage_radius_km ? (
                    <span className="text-muted-foreground/70">· copre {referee.coverage_radius_km} km</span>
                  ) : null}
                </p>
              )}
            </div>

            {/* Rating row */}
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-bold tabular-nums ${tier.text}`}>
                {referee.average_rating ? referee.average_rating.toFixed(1) : '—'}
              </div>
              <div>
                <Stars value={referee.average_rating || 0} />
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {referee.total_reviews || 0} recensioni
                </p>
              </div>
            </div>

            {/* Sports chips */}
            {referee.sports_details?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {referee.sports_details.slice(0, 4).map(sport => (
                  <Badge key={sport.sport_id} variant="secondary" className="text-[11px]">
                    {sport.name}
                  </Badge>
                ))}
                {referee.sports_details.length > 4 && (
                  <Badge variant="outline" className="text-[11px]">
                    +{referee.sports_details.length - 4}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </button>
  );
};

export const RefereesListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referees, setReferees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rating');

  useEffect(() => {
    const fetchReferees = async () => {
      try {
        const res = await api.get('/referees');
        setReferees(res.data);
      } catch (error) {
        console.error('Error fetching referees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReferees();
  }, []);

  const filteredReferees = useMemo(() => {
    const filtered = referees.filter(ref => {
      const matchesSearch =
        ref.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ref.city?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter =
        filter === 'all' ||
        (filter === 'available' && ref.is_available) ||
        (filter === 'top' && (ref.average_rating ?? 0) >= 4);
      return matchesSearch && matchesFilter;
    });

    if (sortBy === 'rating') {
      filtered.sort((a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0));
    } else if (sortBy === 'reviews') {
      filtered.sort((a, b) => (b.total_reviews ?? 0) - (a.total_reviews ?? 0));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || ''));
    }

    return filtered;
  }, [referees, searchQuery, filter, sortBy]);

  const totals = useMemo(() => ({
    total: referees.length,
    available: referees.filter(r => r.is_available).length,
    top: referees.filter(r => (r.average_rating ?? 0) >= 4).length,
  }), [referees]);

  return (
    <div className="pb-24" data-testid="referees-list-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500" />
        <div className="pointer-events-none absolute -top-24 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/15 blur-3xl" />
        <div className="relative px-4 md:px-8 pt-8 pb-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold mb-2">
                  <Shield className="h-3 w-3" />
                  Arbitri
                </span>
                <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                  Trova l&apos;arbitro giusto
                </h1>
                <p className="text-white/85 mt-1 max-w-md">
                  Sfoglia gli arbitri verificati nella tua zona, ordinati per rating e disponibilità.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="px-4 py-3 rounded-xl bg-white/15 backdrop-blur text-white text-center min-w-[80px]">
                  <div className="text-2xl font-bold tabular-nums">{totals.total}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/80">Totale</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white/15 backdrop-blur text-white text-center min-w-[80px]">
                  <div className="text-2xl font-bold tabular-nums text-emerald-200">{totals.available}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/80">Disponibili</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white/15 backdrop-blur text-white text-center min-w-[80px]">
                  <div className="text-2xl font-bold tabular-nums text-amber-200">{totals.top}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/80">Top</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 -mt-6 space-y-6">
        {/* Search & filters */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o città..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')} className="rounded-full">
                Tutti
              </Button>
              <Button size="sm" variant={filter === 'available' ? 'default' : 'outline'} onClick={() => setFilter('available')} className="rounded-full">
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                Disponibili
              </Button>
              <Button size="sm" variant={filter === 'top' ? 'default' : 'outline'} onClick={() => setFilter('top')} className="rounded-full">
                <Star className="h-3.5 w-3.5 mr-1.5" />
                Top rated
              </Button>
              <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3" />
                <span>Ordina:</span>
                <button onClick={() => setSortBy('rating')} className={`underline-offset-2 hover:underline ${sortBy === 'rating' ? 'text-primary font-semibold' : ''}`}>
                  Rating
                </button>
                <span>·</span>
                <button onClick={() => setSortBy('reviews')} className={`underline-offset-2 hover:underline ${sortBy === 'reviews' ? 'text-primary font-semibold' : ''}`}>
                  Recensioni
                </button>
                <span>·</span>
                <button onClick={() => setSortBy('name')} className={`underline-offset-2 hover:underline ${sortBy === 'name' ? 'text-primary font-semibold' : ''}`}>
                  Nome
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        ) : filteredReferees.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white shadow-lg">
                <Shield className="h-7 w-7" />
              </div>
              <h2 className="text-xl font-semibold mb-1">Nessun arbitro trovato</h2>
              <p className="text-muted-foreground">Prova a modificare i filtri o la ricerca</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReferees.map(referee => (
              <RefereeCard
                key={referee.profile_id}
                referee={referee}
                onClick={() => navigate(`/referees/${referee.user_id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RefereesListPage;
